import React, { type JSX } from "react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { MAX_PARAGRAPH_SELECTIONS } from "../../DraftSelectionSlice_Store";

/** Shown when the content YAML could not be parsed. */
export function DraftParseError({ message }: { message: string }): JSX.Element {
  return (
    <div className="h-full overflow-auto p-6 bg-cvc-canvas">
      <div className="max-w-4xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-start space-x-3">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-lg font-semibold text-red-900 mb-2">Invalid YAML Format</h3>
              <p className="text-sm text-red-700 mb-3">
                The content YAML file could not be parsed. Please check the file format.
              </p>
              <pre className="text-xs text-red-600 bg-red-100 p-3 rounded font-mono overflow-x-auto">
                {message}
              </pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Parsed, but without a paragraphs array — fall back to raw YAML. */
export function DraftRawFallback({ contentText }: { contentText: string }): JSX.Element {
  return (
    <div className="h-full overflow-auto p-6 bg-cvc-canvas">
      <div className="max-w-4xl mx-auto">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
          <div className="flex items-start space-x-2">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-yellow-900 mb-1">No Paragraphs Found</h4>
              <p className="text-xs text-yellow-700">
                This content file doesn&apos;t have the expected &quot;paragraphs&quot; array.
                Showing raw content below.
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <pre className="text-xs text-gray-700 font-mono leading-relaxed whitespace-pre-wrap">
            {contentText}
          </pre>
        </div>
      </div>
    </div>
  );
}

/** Toast shown when the selection limit is hit. */
export function SelectionLimitToast(): JSX.Element {
  return (
    <div className="fixed top-4 right-4 z-50 animate-fade-in">
      <div className="bg-orange-50 border border-orange-200 rounded-lg px-4 py-3 shadow-lg max-w-md">
        <div className="flex items-start space-x-3">
          <ExclamationTriangleIcon className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
          <span className="text-sm font-medium text-orange-800">
            Only {MAX_PARAGRAPH_SELECTIONS} paragraphs can be selected at a time for requesting an
            update
          </span>
        </div>
      </div>
    </div>
  );
}
